import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import he from 'he';
import { useContext, useMemo, useState } from 'react';

import Filters from 'components/Filters';
import Loader from 'components/Loader';
import { config } from 'config';
import { ContactModalContext } from 'context/contactModalContext';
import { DataContext } from 'context/dataContext';
import { NavigationContext } from 'context/navigationContext';
import { TranslationContext } from 'context/translationContext';
import {
  balconyOrTerraceFilter,
  directionsFilter,
  flatPlacementFilter,
  floorFilter,
  kitchenFilter,
  priceFilter,
  pricePerMeterFilter,
} from 'tableFilters';
import { Tier } from 'types/Tier';
import { importAllFiles } from 'utils/importAllFiles';

import styles from './styles.module.scss';
import { Props } from './types';

const ApartamentsTable = ({ data }: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { openModal } = useContext(ContactModalContext);
  const { t } = useContext(TranslationContext);
  const { externalApartmentsData, dataLoading } = useContext(DataContext);
  const { setActiveNavigationItem, setSelectedAptId } =
    useContext(NavigationContext);

  const areTilesSelected = useMemo(() => config.tableTiles, []);

  const maxTier: number = useMemo(() => {
    const fetched = importAllFiles(
      require.context('data/edifices', true, /edifice\.json$/),
    );

    // @ts-ignore
    const unique = [...new Set(fetched)];
    if (!unique.length) return 0;

    const maximumTier = unique.reduce((max: number, edifice) => {
      if (!edifice.tiers) return max;

      const allTiers: number[] = edifice.tiers.map((tier: Tier) =>
        tier.name === 'Parter' ? 0 : parseInt(tier.name),
      );

      return Math.max(...allTiers, max);
    }, 0);

    return maximumTier;
  }, []);

  const columnHelper = createColumnHelper<any>();

  const handleBtnClick = (offerNumber: number) => {
    openModal(`${t('interestedIn')} ${offerNumber}, ${t('presentOffer')}`);
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      columnHelper.accessor('number', {
        header: () => <span>{t('no')}</span>,
      }),
      columnHelper.accessor('area', {
        header: () => <span>{t('areaShorthand')}</span>,
        cell: table => (
          <span>
            {table.row.original.area}m<sup>2</sup>
          </span>
        ),
      }),
      columnHelper.accessor('rooms', {
        header: () => <span>{t('rooms')}</span>,
      }),
      columnHelper.accessor('actions', {
        header: () => <span>{t('status')}</span>,
        cell: table => {
          const getButton = (status: string) => (
            <button
              className={[
                styles.btn,
                status !== 'Dostępne' && styles.unavailableBtn,
              ].join(' ')}
              onClick={() => handleBtnClick(table.row.original.number)}
            >
              {status === 'Dostępne' ? t('available') : t('sold')}
            </button>
          );

          if (!externalApartmentsData || !externalApartmentsData.length)
            return getButton(t('askForOffer'));

          const remoteApartment = externalApartmentsData.find(
            apartment =>
              apartment.local_number ===
              table.row.original.number.replace('_', '.'),
          );

          if (remoteApartment && remoteApartment.status_name) {
            const status = he.decode(remoteApartment.status_name);
            return getButton(status);
          }

          return getButton(t('askForOffer'));
        },
      }),
      columnHelper.accessor('balconyOrTerrace', {
        filterFn: balconyOrTerraceFilter,
      }),
      // TODO: price filters
      columnHelper.accessor('price', {
        filterFn: priceFilter,
      }),
      columnHelper.accessor('pricePerMeter', {
        filterFn: pricePerMeterFilter,
      }),
      columnHelper.accessor('floor', {
        filterFn: floorFilter,
      }),
      columnHelper.accessor('kitchen', {
        filterFn: kitchenFilter,
      }),
      columnHelper.accessor('flatPlacement', {
        filterFn: (row, colId, filterValue) =>
          flatPlacementFilter(row, colId, filterValue, maxTier),
      }),
      columnHelper.accessor('directions', {
        filterFn: directionsFilter,
      }),
    ],
    [dataLoading, externalApartmentsData],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility: {
        balconyOrTerrace: false,
        price: false,
        pricePerMeter: false,
        floor: false,
        kitchen: false,
        flatPlacement: false,
        directions: false,
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const getSortIcon = (sortState: string | boolean) => {
    if (typeof sortState === 'string') {
      return (
        <span className={styles.sortIcon}>
          {sortState === 'asc' ? '▲' : '▼'}
        </span>
      );
    }

    return null;
  };

  const handleRowClick = (cell: any) => {
    if (cell.column.id !== 'actions') {
      setSelectedAptId(cell.row.original.id);
      setActiveNavigationItem(3);
    }
  };

  return (
    <>
      <Filters table={table} />
      <div
        className={[
          styles.tableWrapper,
          areTilesSelected && styles.tilesTableWrapper,
        ].join(' ')}
      >
        {dataLoading ? (
          <Loader />
        ) : (
          <table className={styles.container} cellSpacing="0" cellPadding="0">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={[
                            styles.headerWrapper,
                            typeof header.column.getIsSorted() === 'string'
                              ? styles.highlighted
                              : '',
                          ].join(' ')}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {getSortIcon(header.column.getIsSorted())}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className={styles.row}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} onClick={() => handleRowClick(cell)}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default ApartamentsTable;
